import React from 'react'
export default function DieselQuoteModal({ isOpen, onClose, product, onSendQuote }) {
    const [formData, setFormData] = React.useState({
        company_name: '',
        delivery_location: '',
        quantity_required: '',
        contact_person: '',
        contact_number: '',
        email_address: ''
    });
    const [isSending, setIsSending] = React.useState(false);
    const [error, setError] = React.useState(null);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        
        // Validation
        if (!formData.company_name || !formData.delivery_location || !formData.quantity_required ||
            !formData.contact_person || !formData.contact_number || !formData.email_address) {
            setError('Please fill in all fields');
            return;
        }

        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email_address)) {
            setError('Please enter a valid email address');
            return;
        }

        setIsSending(true);
        try {
            await onSendQuote(formData);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSending(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-white rounded-xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">BULK DIESEL REQUEST FORM</h2>
                        <p className="text-sm text-gray-600 mt-1">Product: {product.name}</p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <i className="fas fa-times text-xl"></i>
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="space-y-4">
                        {/* Company Name */}
                        <div>
                            <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-1">
                                Company Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="company_name"
                                name="company_name"
                                value={formData.company_name}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter company name"
                                required
                            />
                        </div>

                        {/* Delivery Location */}
                        <div>
                            <label htmlFor="delivery_location" className="block text-sm font-medium text-gray-700 mb-1">
                                Delivery Location <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                id="delivery_location"
                                name="delivery_location"
                                value={formData.delivery_location}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter delivery location"
                                required
                            />
                        </div>

                        {/* Quantity Required */}
                        <div>
                            <label htmlFor="quantity_required" className="block text-sm font-medium text-gray-700 mb-1">
                                Quantity Required (Litres) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="number"
                                id="quantity_required"
                                name="quantity_required"
                                value={formData.quantity_required}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter quantity in litres"
                                min="1"
                                required
                            />
                        </div>

                        {/* Contact Details Section */}
                        <div className="border-t pt-4 mt-4">
                            <h3 className="text-lg font-semibold text-gray-900 mb-4">CONTACT DETAILS</h3>
                            
                            {/* Contact Person */}
                            <div className="mb-4">
                                <label htmlFor="contact_person" className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Person <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="contact_person"
                                    name="contact_person"
                                    value={formData.contact_person}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter contact person name"
                                    required
                                />
                            </div>

                            {/* Contact Number */}
                            <div className="mb-4">
                                <label htmlFor="contact_number" className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="tel"
                                    id="contact_number"
                                    name="contact_number"
                                    value={formData.contact_number}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter contact number"
                                    required
                                />
                            </div>

                            {/* Email Address */}
                            <div>
                                <label htmlFor="email_address" className="block text-sm font-medium text-gray-700 mb-1">
                                    Email Address <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="email"
                                    id="email_address"
                                    name="email_address"
                                    value={formData.email_address}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="Enter email address"
                                    required
                                />
                            </div>
                        </div>

                        {error && <div className="text-red-500 text-sm bg-red-50 p-3 rounded-lg">{error}</div>}

                        <div className="flex justify-end space-x-4 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="bg-gray-200 text-gray-800 py-2 px-6 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSending}
                                className="primary-gradient text-white py-2 px-6 rounded-lg hover:opacity-90 disabled:opacity-50 transition-colors"
                            >
                                {isSending ? 'Sending...' : 'Submit Request'}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}
